/**
 * « Une nouvelle ère t'attend » — le bandeau du bas (ROADMAP 3.6).
 *
 * ══ Pourquoi ce fichier existe ═══════════════════════════════════════════════
 *
 * Le prestige devenait possible **sans que rien ne le dise**. L'information vivait dans
 * le panneau Progression, c'est-à-dire derrière un tap que le joueur n'a aucune raison
 * de faire à ce moment précis : il est en train de poser des bâtiments, pas de consulter
 * un écran. Le §1 exige qu'à chaque instant il ait sous les yeux un objectif atteignable
 * — et l'ouverture d'une ère est le plus gros de la partie.
 *
 * ══ Trois règles, et elles viennent toutes du même refus ═════════════════════
 *
 * Un bandeau qui s'impose devient une publicité pour son propre jeu. Donc :
 *
 * 1. **jamais bloquant** — il vit au-dessus du dock, la ville reste entière et cliquable ;
 * 2. **refermable**, et il ne revient pas de la session ;
 * 3. **persistant tant que l'ère n'est pas prise** : ce n'est pas une nouvelle qui passe,
 *    c'est un état qui dure. Un toast de trois secondes serait raté par un joueur qui
 *    regarde ailleurs — or il regarde forcément ailleurs, il joue.
 *
 * Le gain est **chiffré** dans le bandeau (« ×1,8 permanent ») : sans le chiffre, la
 * proposition demande d'ouvrir un écran pour savoir si elle vaut le coup, ce qui est
 * exactement le tap qu'on voulait éviter.
 */

import { n, t } from '@/i18n';

import { createButton } from './components';
import type { Component } from './components';
import { icon } from './icons';

export interface EraNoticeCallbacks {
  /** Le joueur veut voir : on l'emmène à l'écran Progression. */
  readonly onOpen: () => void;
}

export interface EraNoticeHandle extends Component {
  /**
   * Aligne le bandeau sur l'état du jeu.
   *
   * @param available l'ère est-elle ouverte ?
   * @param nextMultiplier multiplicateur permanent APRÈS le passage.
   * @param era numéro d'ère courant — un changement rouvre le bandeau refermé.
   */
  update(available: boolean, nextMultiplier: number, era: number): void;
  destroy(): void;
}

export function createEraNotice(callbacks: EraNoticeCallbacks): EraNoticeHandle {
  const el = document.createElement('aside');
  // Nommé, et pas seulement « en bas » : la bannière d'installation porte déjà
  // app-notice--bottom, et une sonde qui viserait la position au lieu du rôle
  // toucherait l'une pour l'autre (règle 22).
  el.className = 'app-notice app-notice--bottom era-notice v-panel v-panel--floating';
  el.hidden = true;
  el.setAttribute('role', 'status');
  // `polite` : la nouvelle est importante, elle n'est pas urgente — elle ne coupe pas
  // la parole à un lecteur d'écran en pleine phrase.
  el.setAttribute('aria-live', 'polite');

  const body = document.createElement('div');
  body.className = 'app-notice__body';

  const title = document.createElement('b');
  title.append(icon('star'), t('prestige.readyTitle'));

  const text = document.createElement('span');
  text.className = 'app-notice__text';

  body.append(title, text);

  const action = createButton({
    label: t('prestige.readyAction'),
    variant: 'primary',
    size: 'sm',
    onClick: () => {
      callbacks.onOpen();
    },
  });

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'v-icon-btn app-notice__close';
  close.setAttribute('aria-label', t('common.close'));
  close.append(icon('close'));

  /** Ère pour laquelle le joueur a refermé le bandeau, `null` s'il ne l'a pas fait. */
  let dismissedEra: number | null = null;

  close.addEventListener('click', () => {
    dismissedEra = shownEra;
    el.hidden = true;
  });

  /** Ère actuellement annoncée — c'est elle que « refermer » vise. */
  let shownEra = 0;

  el.append(body, action.el, close);

  return {
    el,
    update(available, nextMultiplier, era) {
      shownEra = era;

      /*
        Une NOUVELLE ère rouvre le bandeau : le joueur a refermé l'annonce de l'ère 3,
        pas celle de l'ère 4. Sans cette remise à zéro, un seul « fermer » ferait taire
        toutes les ères à venir — et le jeu redeviendrait muet sur son plus gros
        objectif, ce que le §1 interdit.
      */
      if (dismissedEra !== null && dismissedEra !== era) dismissedEra = null;

      const show = available && dismissedEra !== era;
      el.hidden = !show;
      if (!show) return;

      /*
        Le MÊME formateur que le reste du jeu, jamais `toFixed`. Le panneau Prestige
        écrit déjà `n(multiplicateur)` ; deux mises en forme du même nombre finissent
        par diverger, et une ère lointaine afficherait « ×215444.4 » ici et « ×215,4 k »
        là (règle 22).
      */
      text.textContent = t('prestige.readyText', { value: n(nextMultiplier) });
    },
    destroy() {
      el.remove();
    },
  };
}
